<template>
  <div class="relative">
    <nav ref="bottomHeader" class="relative z-[2] flex bg-[color:var(--color-header-bottom-bg)]">
      <div class="container mx-auto flex min-h-[5.5rem] max-w-screen-xl items-center gap-x-5 py-3">
        <router-link :to="$context.settings.default_return_url ?? '/'">
          <VcImage :src="$cfg.logo_image" :alt="$context.storeName" class="h-10" lazy />
        </router-link>

        <!-- Innovadis custom navigation -->
        <div v-if="catalogMenuItems" class="flex items-center lg:ml-4">
          <template v-for="(item, index) in catalogMenuItems" :key="index">
            <component
              :is="item.route ? 'router-link' : 'span'"
              :to="item.route"
              class="mr-4 font-medium text-white hover:text-[color:var(--color-header-bottom-link-hover)]"
            >
              {{ item.title }}
            </component>
          </template>
        </div>

        <SearchBar />

        <ul class="-mx-2 flex items-center">
          <li v-for="item in desktopMainMenuItems" :key="item.id">
            <component :is="(item.id && customLinkComponents[item.id]) || LinkDefault" :item="item" />
          </li>
        </ul>
      </div>
    </nav>

    <!-- Catalog dropdown -->
    <transition
      v-if="catalogMenuItems.length"
      enter-from-class="-translate-y-full"
      leave-to-class="-translate-y-full"
      enter-active-class="will-change-transform"
      leave-active-class="will-change-transform"
    >
      <div
        v-if="catalogMenuVisible"
        ref="catalogMenuElement"
        class="absolute w-full overflow-y-scroll shadow-md transition-transform duration-200"
        :style="catalogMenuStyle"
      >
        <!-- Innovadis custom catalog menu -->
        <CatalogMenu :items="customCatalogItems" @select="catalogMenuVisible = false" />
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside, syncRefs, useElementBounding, useScrollLock } from "@vueuse/core";
import { computed, ref, shallowRef } from "vue";
import { useRouter } from "vue-router";
import { useNavigations } from "@/core/composables";
import { SearchBar } from "@/shared/layout";
import CatalogMenu from "../catalog-menu.vue";
import type { ExtendedMenuLinkType } from "@/core/types";
import type { StyleValue } from "vue";
import { useCustomHeaderLinkComponents } from "@/shared/layout/composables/useCustomHeaderLinkComponents";
import LinkDefault from "@/shared/layout/components/header/_internal/link-components/link-default.vue";

const { catalogMenuItems, desktopMainMenuItems } = useNavigations();
const router = useRouter();
const { customLinkComponents } = useCustomHeaderLinkComponents();

const bottomHeader = ref<HTMLElement | null>(null);
const catalogMenuElement = shallowRef<HTMLElement | null>(null);
const navigation = shallowRef<HTMLElement | null>(null);
const catalogMenuVisible = ref(false);
const customCatalogItems = ref<ExtendedMenuLinkType[]>([]);

const { bottom } = useElementBounding(bottomHeader);

const catalogMenuStyle = computed<StyleValue | undefined>(() =>
  bottom.value ? { maxHeight: `calc(100vh - ${bottom.value}px)` } : undefined,
);

onClickOutside(
  catalogMenuElement,
  () => {
    catalogMenuVisible.value = false;
  },
  { ignore: [navigation] },
);

syncRefs(catalogMenuVisible, useScrollLock(document.body));
</script>
